import {
  AbstractControl,
  FormArray,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

export interface ValidationResult {
  [key: string]: boolean;
}

export function onlyChar(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value == '') return null;
    const re = new RegExp('^[a-zA-Z ]*$');
    if (re.test(control.value)) {
      return null;
    }
    return { onlyChar: true };
  };
}
export function passwordMismatch(password: string, confirmPassword: string) {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const passwordControl = formGroup.get(password);
    const confirmPasswordControl = formGroup.get(confirmPassword);

    if (!passwordControl || !confirmPasswordControl) {
      return null;
    }

    if (
      confirmPasswordControl.errors &&
      !confirmPasswordControl.errors['passwordMismatch']
    ) {
      return null;
    }

    if (passwordControl.value !== confirmPasswordControl.value) {
      confirmPasswordControl.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    confirmPasswordControl.setErrors(null);
    return null;
  };
}
export function passwordStrength(
  control: AbstractControl
): ValidationErrors | null {
  const value: string = control.value || '';

  if (!value) {
    return null;
  }
  const strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
  if (strongRegex.test(value) === false) {
    return {
      passwordStrength: $localize`Le mot de passe doit avoir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial`,
    };
  }

  return null;
}

export function onlyNumric(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == '') return null;
    const re = new RegExp('^[0-9]*$');
    if (re.test(control.value)) {
      return null;
    }
    return { onlyNumric: true };
  };
}

export function checkEmailMobile(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == '') return null;
    const re = new RegExp('^[0-9]*$');
    if (re.test(control.value)) {
      const isPhoneRegexp = new RegExp('^[0-9]{10}$');
      if (isPhoneRegexp.test(control.value)) {
        return null;
      }
      return { isPhoneCheck: true };
    }
    const isEmailRegexp = new RegExp(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
    if (isEmailRegexp.test(control.value)) {
      return null;
    }
    return { isEmailCheck: true };
  };
}
export function decimalNumeric(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == '') return null;
    // let re = new RegExp(/^-?\d*[.,]?\d{0,2}$/);
    const re = new RegExp(/^\d+(\.\d{1,2})?$/);
    if (re.test(control.value)) {
      return null;
    }
    return { decimalNumeric: true };
  };
}

export function moveItemInFormArray(
  formArray: FormArray,
  fromIndex: number,
  toIndex: number
): void {
  const dir = toIndex > fromIndex ? 1 : -1;
  const item = formArray.at(fromIndex);
  for (let i = fromIndex; i * dir < toIndex * dir; i += dir) {
    const current = formArray.at(i + dir);
    formArray.setControl(i, current);
  }
  formArray.setControl(toIndex, item);
}
export function checkEmail(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == '') return null;
    const isEmailRegexp = new RegExp(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
    if (isEmailRegexp.test(control.value)) {
      return null;
    }
    return { isEmailCheck: true };
  };
}

export function checkMobile(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == '') return null;

    const isPhoneRegexp1 = new RegExp('^[0-9]{9,13}$');
    // let isPhoneRegexp2=new RegExp('^[0-9]{13}$');
    if (isPhoneRegexp1.test(control.value)) {
      return null;
    }
    return { isPhoneCheck: true };
  };
}

/**
 * A PDF, judged on the type the browser declared and, when it declared none,
 * on the name. Client-side only: it spares an upload that would be refused,
 * and the server decides for real by opening the file.
 */
export function pdfFile(control: AbstractControl): ValidationErrors | null {
  const file = control.value as File | null;
  if (!file) return null;
  const isPdf =
    file.type === 'application/pdf' ||
    (!file.type && /\.pdf$/i.test(file.name));
  return isPdf ? null : { pdf: true };
}

export function requiredIf(condition: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    condition() ? Validators.required(control) : null;
}

/** Group validator: at least one of the named controls needs a value while the condition holds. */
export function requiredOneOfIf(
  condition: () => boolean,
  ...controlNames: string[]
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null =>
    !condition() || controlNames.some((name) => group.get(name)?.value)
      ? null
      : { requiredOneOf: controlNames };
}

export function sanitizeString(inputString: string): string {
  const sanitizedString = inputString.replace(/[^A-Za-z0-9\s]/g, '');
  return sanitizedString;
}
// min length
export function arrayMinLength(min: number): ValidatorFn | any {
  return (control: AbstractControl[]) => {
    if (!(control instanceof FormArray)) return null;
    return control.length < min ? { minLength: true } : null;
  };
}

export class CustomValidators {
  static strong(control: FormControl): ValidationResult | null {
    const hasNumber = /\d/.test(control.value);
    const hasUpper = /[A-Z]/.test(control.value);
    const hasLower = /[a-z]/.test(control.value);
    const hasSymbol = /[!@#$%^&*()_+={}\\[\]:;,.<>?/-]/.test(control.value);

    const valid =
      hasSymbol &&
      hasNumber &&
      hasUpper &&
      hasLower &&
      control.value?.length >= 12;
    if (!valid) {
      return { strong: true };
    }
    return null;
  }

  /**
   * Validator to check that the first date control is after the second one
   * @param firstDateControlName
   * @param secondDateControlName
   * @returns
   */
  static dateAfter(
    firstDateControlName: string,
    secondDateControlName: string
  ): ValidatorFn {
    return (controls: AbstractControl): ValidationErrors | null => {
      const firstDateControl = controls.get(firstDateControlName);
      const secondDateControl = controls.get(secondDateControlName);

      if (!firstDateControl || !secondDateControl) return null;

      if (firstDateControl.errors && !firstDateControl.errors['dateAfter']) {
        return null;
      }

      if (!firstDateControl.value || !secondDateControl.value) {
        if (firstDateControl.errors?.['dateAfter']) {
          const { dateAfter, ...remainingErrors } = firstDateControl.errors;
          firstDateControl.setErrors(
            Object.keys(remainingErrors).length ? remainingErrors : null
          );
        }
        return null;
      }

      const firstDate = new Date(firstDateControl.value);
      const secondDate = new Date(secondDateControl.value);
      const bothDatesValid =
        !Number.isNaN(firstDate.getTime()) &&
        !Number.isNaN(secondDate.getTime());

      if (!bothDatesValid) return null;

      if (firstDate <= secondDate) {
        firstDateControl.setErrors({
          ...firstDateControl.errors,
          dateAfter: true,
        });
        return { dateAfter: true };
      }

      if (firstDateControl.errors?.['dateAfter']) {
        const { dateAfter, ...remainingErrors } = firstDateControl.errors;
        firstDateControl.setErrors(
          Object.keys(remainingErrors).length ? remainingErrors : null
        );
      }

      return null;
    };
  }

  static ageRange(minAge: number, maxAge: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const dob = new Date(control.value);
      if (Number.isNaN(dob.getTime())) return null;

      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const hasNotHadBirthdayYet =
        today.getMonth() < dob.getMonth() ||
        (today.getMonth() === dob.getMonth() &&
          today.getDate() < dob.getDate());

      if (hasNotHadBirthdayYet) age -= 1;

      if (age > minAge && age < maxAge) return null;

      return { ageRange: true };
    };
  }
}
